import Phaser from 'phaser';
import { ITEM } from '../config/items';

interface Missile {
  ownerId: string;
  startX: number;
  startY: number;
  angle: number;
  firedAt: number;
  gfx: Phaser.GameObjects.Graphics;
  color: number;
}

/**
 * 폭탄 미사일 8발의 궤적·렌더·충돌 질의.
 * 궤적은 (시작좌표, 각도, 발사시각)으로 완전 결정론적이라
 * 모든 클라이언트가 동일하게 재현한다(발사 시점만 동기화).
 */
export class MissileManager {
  private scene: Phaser.Scene;
  private missiles: Missile[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** 8방향(45도 간격) 일제 발사 */
  spawnBurst(ownerId: string, x: number, y: number, color: number): void {
    for (let i = 0; i < ITEM.bomb.missiles; i++) {
      const angle = (i / ITEM.bomb.missiles) * Math.PI * 2;
      const gfx = this.scene.add.graphics();
      gfx.setDepth(12);
      this.missiles.push({
        ownerId,
        startX: x,
        startY: y,
        angle,
        firedAt: Date.now(),
        gfx,
        color,
      });
    }
  }

  /** 위치 갱신 + 사거리 초과 미사일 제거 */
  update(): void {
    const now = Date.now();
    const speed = ITEM.bomb.speed;
    const range = ITEM.bomb.rangePx;

    this.missiles = this.missiles.filter((m) => {
      const traveled = (speed * (now - m.firedAt)) / 1000;
      if (traveled > range) {
        m.gfx.destroy();
        return false;
      }
      const mx = m.startX + Math.cos(m.angle) * traveled;
      const my = m.startY + Math.sin(m.angle) * traveled;

      // 발광체 + 짧은 꼬리
      m.gfx.clear();
      const tx = mx - Math.cos(m.angle) * 18;
      const ty = my - Math.sin(m.angle) * 18;
      m.gfx.lineStyle(6, m.color, 0.5);
      m.gfx.lineBetween(tx, ty, mx, my);
      m.gfx.fillStyle(0xffffff, 1);
      m.gfx.fillCircle(mx, my, 6);
      m.gfx.fillStyle(m.color, 0.9);
      m.gfx.fillCircle(mx, my, 4);
      return true;
    });
  }

  /**
   * (px,py)에 닿은 미사일이 있고 그 발사자가 excludeOwnerId가 아니면 true.
   * 발사자 본인은 자기 미사일에 면역.
   */
  hits(px: number, py: number, radius: number, excludeOwnerId: string): boolean {
    const now = Date.now();
    const speed = ITEM.bomb.speed;
    for (const m of this.missiles) {
      if (m.ownerId === excludeOwnerId) continue;
      const traveled = (speed * (now - m.firedAt)) / 1000;
      const mx = m.startX + Math.cos(m.angle) * traveled;
      const my = m.startY + Math.sin(m.angle) * traveled;
      const dx = mx - px;
      const dy = my - py;
      if (dx * dx + dy * dy <= radius * radius) return true;
    }
    return false;
  }

  /** 임의의 점들(꼬리) 중 하나라도 미사일에 맞으면 발사자 id 반환, 없으면 null */
  hitsAnyPoint(
    points: { x: number; y: number }[],
    radius: number,
    excludeOwnerId: string
  ): string | null {
    const now = Date.now();
    const speed = ITEM.bomb.speed;
    for (const m of this.missiles) {
      if (m.ownerId === excludeOwnerId) continue;
      const traveled = (speed * (now - m.firedAt)) / 1000;
      const mx = m.startX + Math.cos(m.angle) * traveled;
      const my = m.startY + Math.sin(m.angle) * traveled;
      for (const p of points) {
        const dx = mx - p.x;
        const dy = my - p.y;
        if (dx * dx + dy * dy <= radius * radius) return m.ownerId;
      }
    }
    return null;
  }

  destroy(): void {
    for (const m of this.missiles) m.gfx.destroy();
    this.missiles = [];
  }
}
